import _ from 'lodash';
import { defineStore } from 'pinia';
import { DEFAULT_FILTER_STATE } from '@/constants/filter';
import { IQuickFilterFolderViewModel, IQuickFilterItemsViewModel, IQuickFilterViewModel } from '@/models/quickFilter.model';
import { quickFilterDataService } from '@/services/QuickFilterApi';

interface IQuickFilterStore {
  quickFilters: IQuickFilterViewModel[];
  quickFilterFolders: IQuickFilterFolderViewModel[];
  selectedQuickFilter: IQuickFilterViewModel;
  isLoading: boolean;
  isLoadingError: boolean;
}

const selectedQuickFilterKey = "quickFilter";

export const useQuickFilterStore = defineStore('quickFilterStore', {
  state: (): IQuickFilterStore => ({
    quickFilters: [],
    quickFilterFolders: [],
    selectedQuickFilter: getSelectedQuickFilter(),
    isLoading: false,
    isLoadingError: false,
  }),
  getters: {
    isSelectedQuickFilterDefaultState(state) {
      return state.selectedQuickFilter.name === DEFAULT_FILTER_STATE.name;
    }
  },
  actions: {
    getFolderQuickFilters(folderId: string | undefined): IQuickFilterViewModel[] {
      if (!folderId) {
        return this.quickFilters.filter((filter) => !filter.folderId);
      }
      return this.quickFilters.filter((filter) => filter.folderId === folderId);
    },
    setQuickFilterItems(quickFilterItems: IQuickFilterItemsViewModel) {
      this.quickFilters = _.cloneDeep(quickFilterItems.filters);
      this.quickFilters.unshift(_.cloneDeep(DEFAULT_FILTER_STATE));
      this.quickFilterFolders = _.cloneDeep(quickFilterItems.folders);
    },
    async setQuickFilterItemsAsync() {
      if (this.quickFilters?.length) return;

      try {
        this.isLoadingError = false;
        this.isLoading = true;
        const quickFilterItems = await quickFilterDataService.getQuickFilterItemsAsync();
        this.setQuickFilterItems(quickFilterItems);
      }
      catch (error: any) {
        if (error.response?.status === 401) {
          throw error;
        }
        else {
          this.isLoadingError = true;
        }
      }
      finally {
        this.isLoading = false;
      }
    },
    setSelectedQuickFilter(filterState: IQuickFilterViewModel, writeToStorage: boolean = true) {
      this.selectedQuickFilter = _.cloneDeep(filterState);
      if (writeToStorage) this.setSelectedQuickFilterToStorage();
    },
    setSelectedQuickFilterById(quickFilterId: string, writeToStorage: boolean = true) {
      if (!quickFilterId || this.selectedQuickFilter && this.selectedQuickFilter.id === quickFilterId) return;

      const quickFilter = _.find(this.quickFilters, (filter) => filter.id === quickFilterId)
        ?? DEFAULT_FILTER_STATE;

      this.setSelectedQuickFilter(quickFilter, writeToStorage);
    },
    resetSelectedQuickFilter() {
      this.setSelectedQuickFilter(_.cloneDeep(DEFAULT_FILTER_STATE));
    },
    addQuickFilter(quickFilter: IQuickFilterViewModel) {
      if (!quickFilter || !quickFilter.id || !quickFilter.name) {
        return;
      }

      this.quickFilters.push(_.cloneDeep(quickFilter));
      this.sortQuickFilters();
    },
    removeQuickFilter(id: string | undefined) {
      if (!id || !this.quickFilters.length) return;
      
      this.quickFilters = _.remove(this.quickFilters, (filter) => filter.id !== id);
      // Checking if we remove the selected filter
      if (this.selectedQuickFilter.id === id) {
        this.resetSelectedQuickFilter();
      }
    },
    updateQuickFilter(quickFilter: IQuickFilterViewModel) {
      if (!quickFilter || !quickFilter.id || !quickFilter.name) {
        return;
      }

      const quickFilterIndex = _.findIndex(this.quickFilters, (filter) => filter.id === quickFilter.id);
      if (quickFilterIndex === -1) {
        return;
      }

      this.quickFilters[quickFilterIndex] = _.cloneDeep(quickFilter);
      this.sortQuickFilters();
      
      if (quickFilter.id === this.selectedQuickFilter.id) {
        this.setSelectedQuickFilter(quickFilter);
      }
    },
    addQuickFilterFolder(folder: IQuickFilterFolderViewModel) {
      if (!folder || !folder.id || !folder.name) {
        return;
      }

      this.quickFilterFolders.push(_.cloneDeep(folder));
      this.sortQuickFilterFolders();
    },
    removeQuickFilterFolder(id: string | undefined) {
      if (!id || !this.quickFilterFolders.length) {
        return;
      }

      this.quickFilterFolders = _.remove(this.quickFilterFolders, (filter) => filter.id !== id);
      // Clear removed folder id for quick filters
      this.quickFilters.forEach((filter) => {
        if (filter.folderId === id) {
          filter.folderId = undefined;
        }
      });
    },
    updateQuickFilterFolder(folder: IQuickFilterFolderViewModel) {
      if (!folder || !folder.id || !folder.name) {
        return;
      }

      const quickFilterFolderIndex = _.findIndex(this.quickFilterFolders, (filter) => filter.id === folder.id);
      if (quickFilterFolderIndex === -1) {
        return;
      }

      this.quickFilterFolders[quickFilterFolderIndex] = {
        id: folder.id,
        name: folder.name,
      };
      this.sortQuickFilterFolders();
    },
    sortQuickFilters() {
      this.quickFilters = _.sortBy(this.quickFilters, [item => item.isDefault, item => item.name.toLowerCase()]);
    },
    sortQuickFilterFolders() {
      this.quickFilterFolders = _.sortBy(this.quickFilterFolders, [item => item.name.toLowerCase()]);
    },
    setSelectedQuickFilterToStorage() {
      sessionStorage.setItem(selectedQuickFilterKey, JSON.stringify(this.selectedQuickFilter));
    },
  },
});

const getSelectedQuickFilter = () => {
  const selectedQuickFilterString = localStorage.getItem(selectedQuickFilterKey);
  return selectedQuickFilterString
    ? JSON.parse(selectedQuickFilterString)
    : _.cloneDeep(DEFAULT_FILTER_STATE);
};